import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../lib/format';
import { useCodeLabel, useI18n, useLocalized } from '../lib/i18n';
import { can, useSession } from '../lib/session';
import { useDismissFollowup, useFollowups } from '../lib/queries';
import { Button, Card, EmptyState, Pill, SectionTitle, Select, Skeleton, Stat } from '../components/ui';

const HORIZONS = [
  { value: '0', label: 'followups.horizonNow' },
  { value: '7', label: 'followups.horizon7' },
  { value: '14', label: 'followups.horizon14' },
  { value: '30', label: 'followups.horizon30' },
] as const;

export function FollowupsPage() {
  const { t } = useI18n();
  const localized = useLocalized();
  const code = useCodeLabel();
  const { data: session } = useSession();
  const [withinDays, setWithinDays] = useState('14');
  const { data, isPending } = useFollowups(Number(withinDays));
  const dismiss = useDismissFollowup();

  const mayDismiss = can(session, 'customers.update');
  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-1 font-display text-[2rem] font-bold tracking-tight text-ink">{t('followups.title')}</h1>

      {data && (
        <section className="mb-4 grid grid-cols-2 gap-3">
          <Stat label={t('followups.dueNow')} value={data.totals.dueNow} sub={t('followups.dueNowSub')} />
          <Stat label={t('followups.upcoming')} value={data.totals.upcoming} sub={t('followups.upcomingSub')} />
        </section>
      )}

      <div className="mb-4 max-w-xs">
        <Select label={t('common.show')} value={withinDays} onChange={(event) => setWithinDays(event.target.value)}>
          {HORIZONS.map((horizon) => (
            <option key={horizon.value} value={horizon.value}>{t(horizon.label)}</option>
          ))}
        </Select>
      </div>

      {isPending && <Card><Skeleton className="h-24" /></Card>}

      {!isPending && items.length === 0 && (
        <EmptyState
          title={t('followups.empty')}
        />
      )}

      {items.length > 0 && (
        <Card>
          <SectionTitle>{t('followups.count', { count: items.length })}</SectionTitle>
          <div className="space-y-2">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-line bg-raised px-3.5 py-3"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/customers/${item.customerId}`}
                      className="-my-2 inline-block truncate py-2 text-sm font-medium text-ink hover:text-brand"
                    >
                      {item.customerName}
                    </Link>
                    {item.overdue
                      ? <Pill tone="warning">{t('followups.due')}</Pill>
                      : <Pill>{formatDate(item.dueAt)}</Pill>}
                  </div>
                  <div className="mt-1 text-[0.7rem] text-ink-dim">
                    {localized(item.serviceName)}
                    {item.vehicle && ` · ${item.vehicle}`}
                  </div>
                  <div className="tabular mt-0.5 text-[0.68rem] text-ink-faint">
                    {item.phone} · {t('followups.speaks', {
                      lang: code('lang', item.preferredLanguage),
                    })}
                  </div>
                </div>

                <div className="flex shrink-0 gap-1.5">
                  {/* An anchor, not a Button inside one: nesting a button in a
                      link gives keyboard and screen-reader users two targets. */}
                  <a
                    href={`tel:${item.phone}`}
                    className="inline-flex min-h-11 items-center justify-center rounded-lg border border-line bg-raised px-3.5 text-xs font-semibold text-ink transition-colors hover:border-ink-faint hover:bg-muted sm:min-h-8 sm:px-3"
                  >
                    {t('followups.call')}
                  </a>
                  {mayDismiss && (
                    <Button
                      variant="quiet"
                      size="sm"
                      loading={dismiss.isPending && dismiss.variables === item.id}
                      onClick={() => dismiss.mutate(item.id)}
                    >
                      {t('followups.dismiss')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
